import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Box, Button, Heading, Stack, Text } from 'pixelize-design-library';
import { useTranslation } from 'react-i18next';

import { useAppTheme } from '@core/Theme/useAppTheme';
import { canvas, mutedText, statusInk, surface } from '@core/Theme/tokens';

interface ErrorDisplayProps {
  error: Error;
  resetErrorBoundary: () => void;
}

/**
 * A genuine crash — rendered by an ErrorBoundary, which is the only place `error` and
 * `resetErrorBoundary` exist. Never wire this to `path="*"`: both props are undefined
 * there, and the user lands on an unstyled dead end with no way back.
 */
export function ErrorDisplay({ error, resetErrorBoundary }: ErrorDisplayProps) {
  const { colors } = useAppTheme();
  const { t } = useTranslation();

  return (
    <Box
      minH='60vh'
      display='flex'
      alignItems='center'
      justifyContent='center'
      bg={canvas(colors)}
      p={6}
    >
      <Stack
        spacing={4}
        align='center'
        textAlign='center'
        maxW='lg'
        bg={surface(colors)}
        borderRadius='xl'
        borderWidth='1px'
        borderColor={colors.boxborder[200]}
        p={8}
        data-testid='error-display'
      >
        <AlertTriangle size={40} color={statusInk(colors, 'error')} />
        <Heading size='md' color={colors.header[900]}>
          {t('errorPage.crashTitle')}
        </Heading>
        <Text fontSize='sm' color={mutedText(colors)}>
          {error.message || t('errorPage.crashFallback')}
        </Text>
        <Button
          label={t('errorPage.tryAgain')}
          leftIcon={<RotateCcw size={16} />}
          colorScheme='primary'
          onClick={resetErrorBoundary}
          data-testid='error-retry'
        />
      </Stack>
    </Box>
  );
}
